import { cx } from '../lib/cx'
import { orderUrl, useCatalog } from '../lib/catalog'
import { useI18n } from '../lib/i18n'

/** The Telegram paper plane, drawn as a filled mark so it reads at 16px. */
export function TelegramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M21.9 3.4 2.6 10.9c-1 .4-1 1.8.1 2.1l4.6 1.4 1.8 5.6c.3.9 1.4 1.1 2 .4l2.4-2.4 4.5 3.3c.8.6 1.9.2 2.1-.8l3-15.2c.2-1.1-.9-2-2.2-1.9ZM9.5 15.4l-.4 3.2-1.3-4 9.5-6.4-7.8 7.2Z" />
    </svg>
  )
}

export function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
      <path d="M20.5 11.7a8.5 8.5 0 0 1-12.6 7.4L3 20l.9-4.7a8.5 8.5 0 1 1 16.6-3.6Z" />
      <path d="M8.8 8.2c.2-.4.4-.4.7-.4h.4c.2 0 .3.1.4.4l.7 1.7c.1.2.1.4-.1.6l-.5.6c-.2.2-.2.4 0 .6.5.9 1.2 1.6 2.1 2.1.2.1.4.1.6-.1l.6-.7c.2-.2.4-.2.6-.1l1.6.8c.2.1.3.2.3.4 0 .3-.2 1.1-.7 1.5-.5.4-1.1.6-1.8.4-1-.2-2.2-.8-3.4-1.9-1-.9-1.8-2.1-2-3.1-.2-.9 0-1.7.5-2.8Z" />
    </svg>
  )
}

type Size = 'sm' | 'md' | 'lg'

const sizes: Record<Size, string> = {
  sm: 'h-11 rounded-[12px] px-3.5 text-[13px]',
  md: 'h-12 rounded-[14px] px-5 text-[14.5px]',
  lg: 'h-14 rounded-[16px] px-6 text-[16px] sm:h-15',
}

/** Opens a Telegram chat with the shop's user account. */
export function TelegramOrderButton({
  label,
  compactLabel,
  size = 'md',
  tone = 'forest',
  full,
  className,
}: {
  label: string
  /** Shown below the sm breakpoint, where product cards are half width. */
  compactLabel?: string
  size?: Size
  /** "light" is for the button sitting on a dark green panel. */
  tone?: 'forest' | 'light'
  full?: boolean
  className?: string
}) {
  const { settings } = useCatalog()
  const { t } = useI18n()
  const href = orderUrl(settings)

  const shell = cx(
    'group/tele relative isolate inline-flex shrink-0 items-center justify-center gap-2.5 overflow-hidden font-medium tracking-[0.005em]',
    'transition-[transform,background-color,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
    'hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99]',
    'focus-visible:outline-2 focus-visible:outline-offset-2',
    tone === 'forest'
      ? 'bg-forest text-bg shadow-[0_1px_1px_rgba(20,45,31,0.25),0_12px_26px_-16px_rgba(37,75,53,0.75)] hover:bg-forest-deep hover:shadow-[0_2px_3px_rgba(20,45,31,0.2),0_18px_34px_-18px_rgba(37,75,53,0.9)] focus-visible:outline-forest'
      : 'bg-bg text-forest shadow-[0_12px_28px_-16px_rgba(0,0,0,0.5)] hover:bg-white focus-visible:outline-bg',
    sizes[size],
    full && 'w-full',
    !href && 'pointer-events-none opacity-55',
    className,
  )

  const content = (
    <>
      {/* Moss light sweeps across the button on hover. */}
      <span
        aria-hidden
        className="absolute inset-0 -translate-x-[130%] bg-[linear-gradient(100deg,transparent,rgba(166,181,138,0.35),transparent)] transition-transform duration-[900ms] ease-out group-hover/tele:translate-x-[130%]"
      />
      <TelegramIcon
        className={cx(
          'relative size-[18px] transition-[transform,color] duration-300 group-hover/tele:-translate-y-px group-hover/tele:translate-x-0.5',
          tone === 'forest' ? 'text-moss group-hover/tele:text-white' : 'text-forest',
        )}
      />
      {compactLabel ? (
        <>
          <span className="relative sm:hidden">{compactLabel}</span>
          <span className="relative hidden sm:inline">{label}</span>
        </>
      ) : (
        <span className="relative">{label}</span>
      )}
    </>
  )

  if (!href) {
    return (
      <button type="button" disabled className={shell} title={t('telegram_not_configured')}>
        {content}
      </button>
    )
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={shell} aria-label={`${label} — Telegram`}>
      {content}
    </a>
  )
}

/** A quiet inline link for places where a full button would shout. */
export function TelegramHint({ className }: { className?: string }) {
  const { settings } = useCatalog()
  const href = orderUrl(settings)
  if (!href) return null
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cx('inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-forest', className)}
    >
      <TelegramIcon className="size-3.5" />
      {settings.shopUsername ? `@${settings.shopUsername.replace(/^@/, '')}` : 'Telegram'}
    </a>
  )
}
